import { useEffect, useMemo, useState } from 'react'

import { collection, doc, onSnapshot, orderBy, query, Timestamp, writeBatch } from 'firebase/firestore'

import {

  Alert,

  Box,

  Button,

  Checkbox,

  Chip,

  Paper,

  Snackbar,

  Stack,

  Table,

  TableBody,

  TableCell,

  TableContainer,

  TableHead,

  TableRow,

  TextField,

  Typography,

  Pagination,

} from '@mui/material'

import DeleteIcon from '@mui/icons-material/Delete'

import { db } from '../../firebase'



interface AuthPhoneAuditRow {

  id: string

  kind?: string

  phoneNumber?: string

  matched?: boolean

  success?: boolean

  reason?: string

  verificationId?: string

  attemptId?: string

  uid?: string

  error?: string

  createdAt?: Timestamp

}



function formatTs(ts?: Timestamp): string {

  if (!ts) return '-'

  try {

    return ts.toDate().toLocaleString('en-GB')

  } catch (_) {

    return '-'

  }

}



function detailText(r: AuthPhoneAuditRow): string {

  const parts: string[] = []

  if (r.matched != null) parts.push(`matched=${String(r.matched)}`)

  if (r.success != null) parts.push(`success=${String(r.success)}`)

  if (r.reason) parts.push(`reason=${r.reason}`)

  if (r.verificationId) parts.push(`verificationId=${r.verificationId}`)

  if (r.attemptId) parts.push(`attemptId=${r.attemptId}`)

  if (r.uid) parts.push(`uid=${r.uid}`)

  if (r.error) parts.push(`error=${r.error}`)

  return parts.length > 0 ? parts.join(' · ') : '—'

}



const BATCH_LIMIT = 450



export default function AuthPhoneMonitorPage() {

  const [rows, setRows] = useState<AuthPhoneAuditRow[]>([])

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const [page, setPage] = useState(1)

  const pageSize = 25

  const [kindFilter, setKindFilter] = useState('')

  const [phoneFilter, setPhoneFilter] = useState('')

  const [snackbar, setSnackbar] = useState<{

    open: boolean

    message: string

    severity: 'success' | 'error'

  }>({ open: false, message: '', severity: 'success' })



  useEffect(() => {

    const q = query(collection(db, 'auth_phone_audit'), orderBy('createdAt', 'desc'))

    const unsub = onSnapshot(

      q,

      (snap) => {

        setRows(

          snap.docs.map((d) => ({

            id: d.id,

            ...(d.data() as Omit<AuthPhoneAuditRow, 'id'>),

          })),

        )

      },

      (err) => {

        console.error(err)

        setSnackbar({ open: true, message: `فشل الجلب: ${err.message}`, severity: 'error' })

      },

    )

    return () => unsub()

  }, [])



  const filtered = useMemo(() => {

    return rows.filter((r) => {

      const k = (kindFilter || '').trim().toLowerCase()

      const kindOk = !k || (r.kind ?? '').toLowerCase().includes(k)

      const p = phoneFilter.trim().toLowerCase()

      const phoneOk = !p || (r.phoneNumber ?? '').toLowerCase().includes(p)

      return kindOk && phoneOk

    })

  }, [rows, kindFilter, phoneFilter])



  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))

  const paginated = useMemo(

    () => filtered.slice((page - 1) * pageSize, page * pageSize),

    [filtered, page],

  )

  const selectedCount = selectedIds.size



  useEffect(() => {

    if (page > totalPages) setPage(totalPages)

  }, [page, totalPages])



  const isAllCurrentPageSelected =

    paginated.length > 0 && paginated.every((r) => selectedIds.has(r.id))



  const isAllFilteredSelected =

    filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id))



  const toggle = (id: string) => {

    setSelectedIds((prev) => {

      const n = new Set(prev)

      if (n.has(id)) n.delete(id)

      else n.add(id)

      return n

    })

  }



  const toggleSelectCurrentPage = (checked: boolean) => {

    setSelectedIds((prev) => {

      const next = new Set(prev)

      for (const row of paginated) {

        if (checked) next.add(row.id)

        else next.delete(row.id)

      }

      return next

    })

  }



  const selectAllFiltered = () => {

    setSelectedIds(new Set(filtered.map((r) => r.id)))

  }



  const clearSelection = () => {

    setSelectedIds(new Set())

  }



  const deleteSelected = async () => {

    if (selectedIds.size === 0) return

    const ids = Array.from(selectedIds)

    try {

      for (let i = 0; i < ids.length; i += BATCH_LIMIT) {

        const chunk = ids.slice(i, i + BATCH_LIMIT)

        const batch = writeBatch(db)

        chunk.forEach((id) => batch.delete(doc(db, 'auth_phone_audit', id)))

        await batch.commit()

      }

      const count = ids.length

      setSelectedIds(new Set())

      setSnackbar({ open: true, message: `تم حذف ${count} سجل بنجاح.`, severity: 'success' })

    } catch (e: unknown) {

      const msg = e instanceof Error ? e.message : 'فشل الحذف'

      setSnackbar({ open: true, message: msg, severity: 'error' })

    }

  }



  const successCount = rows.filter((r) => r.success === true).length

  const failCount = rows.filter((r) => r.success === false).length



  return (

    <Stack spacing={2}>

      <Box>

        <Typography variant="h5" fontWeight={700}>

          مراقبة الهاتف / إعادة كلمة المرور

        </Typography>

        <Typography variant="body2" color="text.secondary">

          سجلات من الخادم: مطابقة الرنة، وإعادة تعيين كلمة المرور بعد التحقق (بدون عرض كلمات المرور).

        </Typography>

      </Box>



      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>

        <Chip label={`إجمالي: ${rows.length}`} />

        <Chip color="success" label={`نجاح (إن وُجد): ${successCount}`} />

        <Chip color="error" label={`فشل (إن وُجد): ${failCount}`} />

        <Chip label={`المحدد: ${selectedCount}`} variant="outlined" />

        <Button

          variant="outlined"

          size="small"

          disabled={filtered.length === 0 || isAllFilteredSelected}

          onClick={selectAllFiltered}

        >

          تحديد الكل ({filtered.length})

        </Button>

        <Button

          variant="outlined"

          size="small"

          disabled={selectedCount === 0}

          onClick={clearSelection}

        >

          إلغاء التحديد

        </Button>

        <Button

          color="error"

          variant="outlined"

          startIcon={<DeleteIcon />}

          disabled={selectedCount === 0}

          onClick={deleteSelected}

        >

          حذف المحدد ({selectedCount})

        </Button>

      </Stack>



      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>

        <TextField

          size="small"

          label="فلتر نوع الحدث (kind)"

          value={kindFilter}

          onChange={(e) => {

            setKindFilter(e.target.value)

            setPage(1)

          }}

          placeholder="missed_call_incoming"

          sx={{ minWidth: 220 }}

        />

        <TextField

          size="small"

          label="فلتر رقم الهاتف"

          value={phoneFilter}

          onChange={(e) => {

            setPhoneFilter(e.target.value)

            setPage(1)

          }}

          sx={{ minWidth: 220 }}

        />

      </Stack>



      <TableContainer component={Paper} elevation={0}>

        <Table size="small">

          <TableHead>

            <TableRow>

              <TableCell padding="checkbox">

                <Checkbox

                  checked={isAllCurrentPageSelected}

                  indeterminate={selectedCount > 0 && !isAllCurrentPageSelected}

                  onChange={(e) => toggleSelectCurrentPage(e.target.checked)}

                  inputProps={{ 'aria-label': 'تحديد صفحة العرض الحالية' }}

                />

              </TableCell>

              <TableCell>الوقت</TableCell>

              <TableCell>النوع</TableCell>

              <TableCell>الهاتف</TableCell>

              <TableCell>تفاصيل</TableCell>

            </TableRow>

          </TableHead>

          <TableBody>

            {paginated.map((r) => (

              <TableRow key={r.id} hover selected={selectedIds.has(r.id)}>

                <TableCell padding="checkbox">

                  <Checkbox checked={selectedIds.has(r.id)} onChange={() => toggle(r.id)} />

                </TableCell>

                <TableCell>{formatTs(r.createdAt)}</TableCell>

                <TableCell>{r.kind ?? '-'}</TableCell>

                <TableCell>{r.phoneNumber ?? '-'}</TableCell>

                <TableCell sx={{ maxWidth: 420, wordBreak: 'break-word' }}>

                  {detailText(r)}

                </TableCell>

              </TableRow>

            ))}

            {paginated.length === 0 && (

              <TableRow>

                <TableCell colSpan={5} align="center">

                  لا توجد سجلات بعد (يُملأ المجموعة تلقائياً من الدوال السحابية).

                </TableCell>

              </TableRow>

            )}

          </TableBody>

        </Table>

      </TableContainer>



      <Stack direction="row" justifyContent="center">

        <Pagination color="primary" page={page} count={totalPages} onChange={(_, v) => setPage(v)} />

      </Stack>



      <Snackbar

        open={snackbar.open}

        autoHideDuration={6000}

        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}

      >

        <Alert

          severity={snackbar.severity}

          variant="filled"

          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}

        >

          {snackbar.message}

        </Alert>

      </Snackbar>

    </Stack>

  )

}


